import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import {
  getManualQueue,
  manualQueueKeys,
  submitManualQueueAction,
  type ManualQueueItem,
  type ManualQueueKey,
} from '@/services/api/requests/manualQueues';
import {
  Badge,
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';

const queueLabels: Record<ManualQueueKey, string> = {
  security_conflicts: 'Security conflicts',
  sanitizations: 'Sanitizations',
  block_reasons: 'Legacy unknown & block reasons',
  invitation_claims: 'Pre-create claim conflicts',
  lifecycle_locks: 'Locks past alarm threshold',
  repair_rows: 'Repair rows',
};

const queueDescriptions: Record<ManualQueueKey, string> = {
  security_conflicts: 'Review invitation classification, rotation, sanitization, and accepted-state repair.',
  sanitizations: 'Review open sanitizations and cases waiting for the sweep.',
  block_reasons: 'Review legacy_unknown and individual block reasons.',
  invitation_claims: 'Review claims in conflict before adoption, removal, or disproval.',
  lifecycle_locks: 'Review locks held longer than the backend alarm threshold.',
  repair_rows: 'Review repair rows in groups 0 and 5, plus any repair row marked “needs manual”.',
};

const renderJson = (value: unknown) => {
  if (value instanceof Error) return value.message;
  return JSON.stringify(value, null, 2) ?? 'No data recorded';
};

const age = (milliseconds: number | null) => {
  if (milliseconds === null || !Number.isFinite(milliseconds)) return '—';
  return formatDistanceToNow(Date.now() - milliseconds);
};

const ManualQueuesPage = () => {
  const [active, setActive] = useState<ManualQueueKey>(manualQueueKeys[0]);
  const [selected, setSelected] = useState<ManualQueueItem | null>(null);
  const [action, setAction] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<unknown>(null);
  const [pages, setPages] = useState<Record<ManualQueueKey, number>>(
    () => Object.fromEntries(manualQueueKeys.map((key) => [key, 1])) as Record<ManualQueueKey, number>,
  );
  const queryClient = useQueryClient();
  const queueResults = useQueries({
    queries: manualQueueKeys.map((key) => ({
      queryKey: ['super-admin', 'manual-queue', key, pages[key]],
      queryFn: () => getManualQueue(key, pages[key]),
      refetchInterval: 60_000,
    })),
  });
  const mutation = useMutation({
    mutationFn: submitManualQueueAction,
    onSuccess: (response) => {
      setResult(response);
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'manual-queue'] });
    },
    onError: (error) => {
      // The API client rejects with the backend response body, including refusals.
      setResult(error);
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'manual-queue'] });
    },
  });

  const openItem = (item: ManualQueueItem) => {
    setSelected(item);
    setAction(null);
    setReason('');
    setResult(null);
  };

  const closeItem = () => {
    if (mutation.isPending) return;
    setSelected(null);
  };

  const submit = () => {
    if (!selected || !action || !reason.trim() || mutation.isPending || result !== null) return;
    mutation.mutate({
      queue: selected.queue,
      id: selected.id,
      version: selected.version,
      action,
      reason: reason.trim(),
    });
  };

  return (
    <SidebarItemViewTemplate title="Phase 0 manual queues">
      <VStack align="stretch" width="100%" padding="40px" gap="20px" borderRadius={'20px'}>
        <Box bg="white" borderRadius="16px" padding="40px" boxShadow={'0px 4px 20px rgba(0, 0, 0, 0.1)'}>
          <HStack justify="space-between" align="start" flexWrap="wrap" gap="12px">
            <Box>
              <Text variant={'urbanistSemiBold'} color={'text.darkBlue'} fontSize={'26px'} textAlign={'start'}>
                Phase 0 manual queues
              </Text>
              <Text color="text.mediumGray">Cases from the current API environment</Text>
            </Box>
            <Button
              onClick={() => queueResults.forEach((query) => query.refetch())}
              isLoading={queueResults.some((query) => query.isFetching)}
              size="sm"
            >
              Refresh
            </Button>
          </HStack>
          {queueResults.some((query) => query.isError) && (
            <Text color="extra.red" mt="16px">
              Some queues could not be loaded. Check the selected API environment and retry.
            </Text>
          )}
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap="12px" mt="20px">
            {manualQueueKeys.map((key, index) => {
              const queue = queueResults[index].data;
              return (
                <Box key={key} borderWidth="1px" borderRadius="10px" padding="16px">
                  <Text fontWeight="bold">{queueLabels[key]}</Text>
                  <HStack mt="8px" gap="12px">
                    <Badge colorScheme="red">Blocks Phase 0: {queue?.blocksPhase0 ?? '—'}</Badge>
                    <Text fontSize="sm">Total: {queue?.total ?? '—'}</Text>
                  </HStack>
                  <Text fontSize="sm" mt="8px">
                    Oldest blocker: {age(queue?.oldestBlockingAgeMs ?? null)}
                  </Text>
                </Box>
              );
            })}
          </SimpleGrid>
        </Box>

        <Box bg="white" borderRadius="16px" padding="24px" boxShadow="sm">
          <Tabs index={manualQueueKeys.indexOf(active)} onChange={(index) => setActive(manualQueueKeys[index])} isLazy>
            <TabList overflowX="auto">
              {manualQueueKeys.map((key) => (
                <Tab key={key} whiteSpace="nowrap">
                  {queueLabels[key]}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              {manualQueueKeys.map((key, index) => {
                const queue = queueResults[index].data;
                return (
                  <TabPanel key={key} px={0}>
                    <Text color="text.mediumGray" mb="16px">
                      {queueDescriptions[key]}
                    </Text>
                    {queueResults[index].isPending ? (
                      <Text>Loading cases…</Text>
                    ) : !queue ? (
                      <Text>Queue unavailable.</Text>
                    ) : queue.items.length === 0 ? (
                      <Text>No cases in this queue.</Text>
                    ) : (
                      <VStack align="stretch" gap="10px">
                        {queue.items.map((item) => (
                          <HStack
                            key={item.id}
                            borderWidth="1px"
                            borderRadius="10px"
                            padding="14px"
                            justify="space-between"
                            flexWrap="wrap"
                          >
                            <Box>
                              <Text fontWeight="bold">{item.id}</Text>
                              <Text fontSize="sm">
                                {item.state} · In queue {age(item.age_ms)} · Version {item.version}
                              </Text>
                            </Box>
                            <HStack>
                              <Badge colorScheme={item.blocks_phase0 ? 'red' : 'gray'}>
                                {item.blocks_phase0 ? 'Blocks Phase 0' : 'Nonblocking'}
                              </Badge>
                              <Button size="sm" onClick={() => openItem(item)}>
                                Review
                              </Button>
                            </HStack>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                    {queue && queue.totalPages > 1 && (
                      <HStack justify="end" mt="16px">
                        <Button
                          size="sm"
                          isDisabled={pages[key] <= 1}
                          onClick={() => setPages((current) => ({ ...current, [key]: current[key] - 1 }))}
                        >
                          Previous
                        </Button>
                        <Text fontSize="sm">
                          Page {queue.page} of {queue.totalPages}
                        </Text>
                        <Button
                          size="sm"
                          isDisabled={pages[key] >= queue.totalPages}
                          onClick={() => setPages((current) => ({ ...current, [key]: current[key] + 1 }))}
                        >
                          Next
                        </Button>
                      </HStack>
                    )}
                  </TabPanel>
                );
              })}
            </TabPanels>
          </Tabs>
        </Box>
      </VStack>
      <Modal isOpen={selected !== null} onClose={closeItem} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Review {selected?.id}</ModalHeader>
          <ModalCloseButton isDisabled={mutation.isPending} />
          <ModalBody pb="24px">
            <VStack align="stretch" gap="16px">
              <Text fontSize="sm">
                Recorded version: {selected?.version} ·{' '}
                {selected?.blocks_phase0 ? 'Blocks Phase 0' : 'Does not block Phase 0'}
              </Text>
              <Box>
                <Text fontWeight="bold" mb="6px">
                  Backend evidence snapshot
                </Text>
                <Box
                  as="pre"
                  whiteSpace="pre-wrap"
                  overflowWrap="anywhere"
                  bg="gray.50"
                  padding="12px"
                  borderRadius="8px"
                  fontSize="sm"
                >
                  {renderJson(selected?.evidence)}
                </Box>
              </Box>
              {result === null ? (
                <>
                  <Box>
                    <Text fontWeight="bold" mb="6px">
                      Allowed actions
                    </Text>
                    <HStack flexWrap="wrap">
                      {selected?.allowed_actions.map((allowed) => (
                        <Button
                          key={allowed}
                          size="sm"
                          variant={action === allowed ? 'solid' : 'outline'}
                          colorScheme="blue"
                          onClick={() => setAction(allowed)}
                        >
                          {allowed}
                        </Button>
                      ))}
                    </HStack>
                    {selected?.allowed_actions.length === 0 && <Text>No action currently allowed by the backend.</Text>}
                  </Box>
                  <Box>
                    <Text as="label" htmlFor="manual-action-reason" fontWeight="bold">
                      Reason (required)
                    </Text>
                    <Textarea
                      id="manual-action-reason"
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      mt="6px"
                    />
                  </Box>
                  <HStack justify="end">
                    <Button onClick={closeItem} variant="ghost">
                      Cancel
                    </Button>
                    <Button
                      colorScheme="blue"
                      onClick={submit}
                      isDisabled={!action || !reason.trim()}
                      isLoading={mutation.isPending}
                    >
                      Submit action
                    </Button>
                  </HStack>
                </>
              ) : (
                <>
                  <Box>
                    <Text fontWeight="bold">Backend result or refusal</Text>
                    <Box
                      as="pre"
                      whiteSpace="pre-wrap"
                      overflowWrap="anywhere"
                      bg="gray.50"
                      padding="12px"
                      borderRadius="8px"
                      fontSize="sm"
                    >
                      {renderJson(result)}
                    </Box>
                  </Box>
                  <Button onClick={closeItem}>Close</Button>
                </>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </SidebarItemViewTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_admin/super-admin/manual-queues')({
  component: ManualQueuesPage,
});
